import { VideoCameraSlashIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useScanFeedback } from '@/hooks/useScanFeedback';

const SCANNER_ELEMENT_ID = 'barcode-scanner-reader';

type ScannerStatus = 'scanning' | 'loading' | 'not_found' | 'camera_error';

interface BarcodeScannerProps {
    onClose: () => void;
}

export function BarcodeScanner({ onClose }: BarcodeScannerProps) {
    const navigate = useNavigate();
    const [
        t,
    ] = useTranslation('pages');

    const { isFlashing, triggerFeedback } = useScanFeedback();

    const [
        status,
        setStatus,
    ] = useState<ScannerStatus>('scanning');

    const scannerRef = useRef<Html5Qrcode | null>(null);

    const stopScanner = async () => {
        if (scannerRef.current) {
            await scannerRef.current.stop().catch(() => {});
            scannerRef.current.clear();
            scannerRef.current = null;
        }
    };

    const handleClose = async () => {
        await stopScanner();
        onClose();
    };

    const handleScanAgain = () => {
        setStatus('scanning');
    };

    const onDecoded = async (ean: string) => {
        await stopScanner();
        triggerFeedback();
        setStatus('loading');

        await axios.get('/api/products', { params: { ean } }).then(({ data }) => {
            const product = data.results?.[0];

            if (!product) {
                setStatus('not_found');

                return;
            }

            onClose();
            navigate(`/product/${ product.uuid }`);
        }).catch(() => {
            setStatus('not_found');
        });
    };

    useEffect(() => {
        if (status !== 'scanning') {
            return;
        }

        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
            verbose: false,
            formatsToSupport: [
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.CODE_128,
            ],
        });

        scannerRef.current = scanner;

        scanner.start(
            { facingMode: 'environment' },
            {
                fps: 15,
                qrbox: { width: 300, height: 100 },
                videoConstraints: {
                    facingMode: 'environment',
                    focusMode: { ideal: 'continuous' },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    advanced: [
                        { zoom: 1.5 },
                    ],
                } as unknown as MediaTrackConstraints,
            },
            (decodedText: string) => {
                onDecoded(decodedText);
            },
            () => {}
        )
            // Camera permission denied/unavailable, no camera found, or an insecure context — surfaced
            // as its own state rather than left silently stuck on the "scanning" reticle forever.
            .catch(() => {
                setStatus('camera_error');
            });

        return () => {
            stopScanner();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        status,
    ]);

    return (
        <div className='fixed inset-0 z-50 bg-black flex flex-col'>
            {/* Close button */}
            <button
                aria-label={ t('Close scanner') }
                className='absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors'
                onClick={ handleClose }
                type='button'
            >
                <XMarkIcon className='h-6 w-6' />
            </button>

            {/* Camera feed */}
            {status === 'scanning' && (
                <div className='flex-1 flex flex-col items-center justify-center'>
                    <div className='relative w-full max-w-sm'>
                        {/* html5-qrcode attaches the video feed here. [canvas] is hidden to prevent the processing canvas from rendering alongside the video. */}
                        <div className='w-full [&_canvas]:hidden' id={ SCANNER_ELEMENT_ID } />

                        {/* Reticle overlay */}
                        <div className='absolute inset-0 pointer-events-none flex flex-col items-center justify-center gap-4'>
                            <div className={ `w-[300px] h-[100px] rounded-lg border-2 transition-colors duration-150 ${ isFlashing ? 'border-success/40 shadow-[0_0_16px_4px_rgba(74,222,128,0.5)]' : 'border-white/70' }` } />
                            <p className='text-white/70 text-sm'>{t('scanner.aim_at_barcode')}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation panel */}
            {status !== 'scanning' && (
                <div className='flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center'>
                    {status === 'loading' && (
                        <>
                            <div className='h-16 w-16 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                            <div>
                                <p className='text-white text-lg font-semibold'>{t('scanner.searching_product')}</p>
                                <p className='text-white/60 text-sm mt-1'>{t('scanner.loading_product')}</p>
                            </div>
                        </>
                    )}

                    {status === 'not_found' && (
                        <>
                            <XCircleIcon className='h-16 w-16 text-destructive' />
                            <div>
                                <p className='text-white text-lg font-semibold'>{t('scanner.not_found')}</p>
                                <p className='text-white/60 text-sm mt-1'>{t('scanner.not_found_description')}</p>
                            </div>
                            <button
                                className='px-6 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors'
                                onClick={ handleScanAgain }
                                type='button'
                            >
                                {t('scanner.scan_again')}
                            </button>
                        </>
                    )}

                    {status === 'camera_error' && (
                        <>
                            <VideoCameraSlashIcon className='h-16 w-16 text-destructive' />
                            <div>
                                <p className='text-white text-lg font-semibold'>{t('scanner.camera_error')}</p>
                                <p className='text-white/60 text-sm mt-1'>{t('scanner.camera_error_description')}</p>
                            </div>
                            <button
                                className='px-6 py-2.5 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors'
                                onClick={ handleScanAgain }
                                type='button'
                            >
                                {t('scanner.scan_again')}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
